import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { api } from "../lib/api";
import RevealText from "../components/RevealText";
import ScrollReveal from "../components/ScrollReveal";
import KorvJar from "../components/KorvJar";
import { toast } from "sonner";

export default function RitualDetail() {
  const { slug } = useParams();
  const [ritual, setRitual] = useState(null);
  const [all, setAll] = useState([]);
  const [selectedCode, setSelectedCode] = useState("50");   // "50" | "15" | "duo"
  const [open, setOpen] = useState(false);

  useEffect(() => {
    api.get(`/rituals/${slug}`).then((r) => setRitual(r.data)).catch(() => {});
    api.get(`/rituals`).then((r) => setAll(r.data)).catch(() => {});
  }, [slug]);

  if (!ritual) return (
    <div className="min-h-[100svh] bg-ink text-bone flex items-center justify-center">
      <span className="text-[11px] uppercase tracking-editorial text-bone/55">Loading —</span>
    </div>
  );

  const selectedPrice = selectedCode === "duo"
    ? ritual.duo_price
    : ritual.sizes.find((s) => s.code === selectedCode)?.price_display;

  const selectedLabel = selectedCode === "duo"
    ? "Home & Away Duo"
    : ritual.sizes.find((s) => s.code === selectedCode)?.label;

  const next = all.find((x) => x.slug !== ritual.slug && x.order === (ritual.order % Math.max(all.length, 1)) + 1);

  return (
    <div className="bg-ink text-bone" data-testid={`ritual-page-${ritual.slug}`}>
      {/* Meta bar */}
      <div className="max-w-[1600px] mx-auto px-6 md:px-10 pt-24 md:pt-28 py-6 flex items-center justify-between text-[10px] uppercase tracking-editorial text-bone/60">
        <span>Volume II · Deux Rituels · N° 00{ritual.order}</span>
        <Link to="/rituals" className="editorial-link !text-bone !border-bone/60">Back to the Trio</Link>
      </div>
      <div className="h-px bg-bone/15" />

      {/* ─── HERO — split canvas ─── */}
      <section className="grid grid-cols-12 min-h-[92svh]">
        <div className="col-span-12 md:col-span-6 relative overflow-hidden" style={{ background: shade(ritual.swatch_hex, -6) }}>
          {/* Spotlight */}
          <div aria-hidden className="absolute inset-0"
               style={{ background: "radial-gradient(55% 55% at 50% 45%, rgba(255,240,220,0.10), transparent 70%)" }} />
          {/* Floor gradient */}
          <div aria-hidden className="absolute inset-x-0 bottom-0 h-[30%]" style={{
            background: `linear-gradient(180deg, rgba(0,0,0,0), rgba(0,0,0,0.5))`,
          }} />

          <AnimatePresence mode="wait">
            <motion.div
              key={selectedCode}
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -24 }}
              transition={{ duration: 1.1, ease: [0.2, 0.7, 0.1, 1] }}
              className="absolute inset-0 flex items-center justify-center gap-6 md:gap-10 p-10"
            >
              {selectedCode === "duo" ? (
                <>
                  <div className="w-[220px] md:w-[280px]">
                    <KorvJar color={ritual.swatch_hex} accent={ritual.accent_hex} variant="signature"
                             sublabel={`${ritual.name.toUpperCase()} · 50 ML`} animate={false} />
                  </div>
                  <div className="w-[130px] md:w-[170px]">
                    <KorvJar color={ritual.swatch_hex} accent={ritual.accent_hex} variant="travel"
                             sublabel={`${ritual.name.toUpperCase()} · 15 ML`} animate={false} />
                  </div>
                </>
              ) : selectedCode === "15" ? (
                <div className="w-[220px] md:w-[280px]">
                  <KorvJar color={ritual.swatch_hex} accent={ritual.accent_hex} variant="travel"
                           sublabel={`${ritual.name.toUpperCase()} · 15 ML`} animate={false} />
                </div>
              ) : (
                <div className="w-[280px] md:w-[380px]">
                  <KorvJar color={ritual.swatch_hex} accent={ritual.accent_hex} variant="signature"
                           sublabel={`${ritual.name.toUpperCase()} · 50 ML`} animate={false} />
                </div>
              )}
            </motion.div>
          </AnimatePresence>

          <div className="absolute left-6 top-6 text-[10px] uppercase tracking-editorial text-bone/70">
            <span className="inline-block w-2 h-2 mr-2" style={{ background: ritual.accent_hex, borderRadius: 999 }} />
            Ritual Balm
          </div>
          <div className="absolute right-6 top-6 text-[10px] uppercase tracking-editorial text-bone/60">
            {selectedLabel}
          </div>
        </div>

        {/* Right — copy + size selector */}
        <div className="col-span-12 md:col-span-6 flex flex-col justify-between p-8 md:p-16 md:pl-20 relative">
          <div className="text-[10px] uppercase tracking-editorial text-bone/60 flex items-center justify-between">
            <span>korv.</span>
            <span>{ritual.notes.join("  ·  ")}</span>
          </div>

          <div className="my-12 md:my-0">
            <p className="text-[11px] uppercase tracking-eyebrow mb-6" style={{ color: ritual.accent_hex }}>
              {ritual.tagline}
            </p>
            <RevealText
              as="h1"
              className="font-serif-display text-[18vw] md:text-[11vw] leading-[0.86] tracking-[-0.03em]"
              lines={[ritual.name]}
            />
            <p className="mt-10 text-[17px] leading-[1.65] max-w-[46ch] text-bone/80">
              {ritual.description_long}
            </p>

            {/* Size Selector */}
            <div className="mt-12">
              <p className="text-[10px] uppercase tracking-editorial text-bone/55 mb-4">Choose your ritual</p>
              <div className="grid grid-cols-1 gap-3 max-w-md">
                {ritual.sizes.map((s) => (
                  <SizeOption
                    key={s.code}
                    active={selectedCode === s.code}
                    onClick={() => setSelectedCode(s.code)}
                    testId={`size-${s.code}`}
                    label={s.label}
                    sub={s.volume}
                    price={s.price_display}
                  />
                ))}
                <SizeOption
                  active={selectedCode === "duo"}
                  onClick={() => setSelectedCode("duo")}
                  testId="size-duo"
                  label="Home & Away Duo"
                  sub="50 ml Signature + 15 ml Travel"
                  price={ritual.duo_price}
                  featured
                  accent={ritual.accent_hex}
                />
              </div>
            </div>

            <div className="mt-10 flex items-end gap-8">
              <span className="chapter-num text-4xl md:text-5xl">{selectedPrice}</span>
              <button
                data-testid="ritual-enquire"
                onClick={() => setOpen(true)}
                className="editorial-link text-[11px] uppercase tracking-editorial pb-2 border-b border-bone"
              >
                Reserve · Enquire
              </button>
            </div>
          </div>

          <div className="text-[10px] uppercase tracking-editorial text-bone/55 flex items-center justify-between">
            <span>Filled by hand · Charlotte</span>
            <span>Weighted matte glass</span>
            <span>Leak-proof seal</span>
          </div>
        </div>
      </section>

      {/* ─── FORMULATION ─── */}
      <section className="py-24 md:py-40 border-t border-bone/15">
        <div className="max-w-[1600px] mx-auto px-6 md:px-10 grid grid-cols-12 gap-6 md:gap-10">
          <ScrollReveal className="col-span-12 md:col-span-5">
            <p className="text-[10px] uppercase tracking-eyebrow text-bone/55 mb-6">On the formulation</p>
            <h3 className="font-serif-display text-3xl md:text-5xl leading-[1.05] max-w-[18ch]">
              Dense, whipped, <span className="italic font-serif-italic">deeply restorative</span>.
            </h3>
          </ScrollReveal>
          <ScrollReveal delay={0.1} className="col-span-12 md:col-span-6 md:col-start-7">
            <p className="text-[17px] leading-[1.7] text-bone/80 max-w-[52ch]">{ritual.formulation_note}</p>
            <div className="mt-10 grid grid-cols-2 gap-6">
              {ritual.notes.map((n, i) => (
                <div key={i} className="border-t border-bone/25 pt-4">
                  <p className="text-[10px] uppercase tracking-editorial text-bone/55">Note 0{i + 1}</p>
                  <p className="font-serif-display text-2xl md:text-3xl mt-2">{n}</p>
                </div>
              ))}
            </div>
          </ScrollReveal>
        </div>
      </section>

      {/* ─── RITUAL ─── */}
      <section className="py-24 md:py-32 border-t border-bone/15">
        <div className="max-w-[1600px] mx-auto px-6 md:px-10 grid grid-cols-12 gap-6 md:gap-10">
          <ScrollReveal className="col-span-12 md:col-span-5">
            <p className="text-[10px] uppercase tracking-eyebrow text-bone/55 mb-8">The Ritual</p>
            <ol className="space-y-8">
              {ritual.ritual_steps.map((step, i) => (
                <li key={i} className="border-b border-bone/20 pb-6">
                  <div className="text-[10px] uppercase tracking-editorial text-bone/55">Step {i + 1}</div>
                  <p className="font-serif-display text-2xl md:text-3xl leading-[1.15] mt-2">{step}</p>
                </li>
              ))}
            </ol>
          </ScrollReveal>
          <ScrollReveal delay={0.1} className="col-span-12 md:col-span-6 md:col-start-7">
            <div className="relative rounded-none overflow-hidden aspect-[4/5]" style={{ background: shade(ritual.swatch_hex, -8) }}>
              <div aria-hidden className="absolute inset-0"
                   style={{ background: "radial-gradient(50% 50% at 50% 45%, rgba(255,240,220,0.08), transparent 65%)" }} />
              <div aria-hidden className="absolute inset-x-0 bottom-0 h-[30%]" style={{
                background: `linear-gradient(180deg, rgba(0,0,0,0), rgba(0,0,0,0.5))`,
              }} />
              <div className="absolute inset-0 flex items-end justify-center gap-6 pb-16">
                <div className="w-[62%]"><KorvJar color={ritual.swatch_hex} accent={ritual.accent_hex} variant="signature" sublabel={`${ritual.name.toUpperCase()} · 50 ML`} animate={false} /></div>
                <div className="w-[36%] mb-2"><KorvJar color={ritual.swatch_hex} accent={ritual.accent_hex} variant="travel" sublabel={`${ritual.name.toUpperCase()} · 15 ML`} animate={false} /></div>
              </div>
              <div className="absolute left-4 top-4 text-[10px] uppercase tracking-editorial text-bone/60">Fig. II — The Pair</div>
              <div className="absolute right-4 top-4 text-[10px] uppercase tracking-editorial text-bone/50">korv.</div>
            </div>
          </ScrollReveal>
        </div>
      </section>

      {/* ─── NEXT ─── */}
      {next && (
        <section className="border-t border-bone/15 py-20 md:py-28">
          <div className="max-w-[1600px] mx-auto px-6 md:px-10 flex items-end justify-between">
            <div>
              <p className="text-[10px] uppercase tracking-eyebrow text-bone/55 mb-6">Next in the trio</p>
              <Link to={`/rituals/${next.slug}`} className="font-serif-display text-5xl md:text-7xl group inline-flex items-baseline gap-6">
                {next.name} <span className="text-[11px] tracking-editorial uppercase text-bone/60">→</span>
              </Link>
            </div>
            <span className="hidden md:inline text-[10px] uppercase tracking-editorial text-bone/60">{next.notes[0]}</span>
          </div>
        </section>
      )}

      {open && (
        <EnquireDialog
          ritual={ritual}
          variant={selectedCode}
          variantLabel={selectedLabel}
          price={selectedPrice}
          onClose={() => setOpen(false)}
        />
      )}
    </div>
  );
}

function SizeOption({ active, onClick, label, sub, price, featured, accent, testId }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testId}
      className={`text-left px-5 py-4 border transition-all duration-500 ${
        active ? "border-bone bg-bone/10" : "border-bone/30 hover:border-bone/70"
      }`}
    >
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          {featured && <span className="inline-block w-1.5 h-1.5" style={{ background: accent, borderRadius: 999 }} />}
          <span className="text-[13px] uppercase tracking-editorial">{label}</span>
        </div>
        <span className="chapter-num text-[15px]">{price}</span>
      </div>
      <p className="mt-1 text-[11px] text-bone/55 tracking-wide">{sub}</p>
    </button>
  );
}

function EnquireDialog({ ritual, variant, variantLabel, price, onClose }) {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [message, setMessage] = useState("");
  const [state, setState] = useState("idle");

  const submit = async (e) => {
    e.preventDefault();
    setState("loading");
    try {
      await api.post("/enquiries", {
        product_slug: `ritual-${ritual.slug}`,
        variant,
        name, email, message,
      });
      toast.success("Received. The house will be in quiet correspondence.");
      setState("ok");
      setTimeout(onClose, 900);
    } catch {
      toast.error("A quiet issue. Kindly try again.");
      setState("idle");
    }
  };

  return (
    <motion.div
      data-testid="ritual-enquire-dialog"
      initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      className="fixed inset-0 z-[70] bg-ink/75 backdrop-blur-sm flex items-center justify-center px-6"
      onClick={onClose}
    >
      <motion.div
        initial={{ y: 30, opacity: 0 }} animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.6, ease: [0.2, 0.7, 0.1, 1] }}
        className="w-full max-w-xl bg-bone text-ink p-8 md:p-12 relative"
        onClick={(e) => e.stopPropagation()}
      >
        <button onClick={onClose} className="absolute top-4 right-4 text-[11px] uppercase tracking-editorial">Close</button>
        <p className="text-[10px] uppercase tracking-eyebrow text-ink/60 mb-4">Reserve · Enquire</p>
        <h3 className="font-serif-display text-3xl md:text-5xl leading-[1.05] mb-2">{ritual.name}</h3>
        <p className="text-[13px] text-ink/70 mb-8">{variantLabel} · {price}</p>
        <form onSubmit={submit} className="space-y-6">
          <input data-testid="ritual-enquire-name" value={name} onChange={(e)=>setName(e.target.value)} required placeholder="Your name" className="field" />
          <input data-testid="ritual-enquire-email" type="email" value={email} onChange={(e)=>setEmail(e.target.value)} required placeholder="Your address" className="field" />
          <textarea data-testid="ritual-enquire-message" value={message} onChange={(e)=>setMessage(e.target.value)} placeholder="A note (optional)" rows={3} className="field resize-none" />
          <button data-testid="ritual-enquire-submit" disabled={state==="loading"} className="mt-2 text-[11px] uppercase tracking-editorial pb-2 border-b border-ink">
            {state === "loading" ? "Sending —" : "Send Enquiry"}
          </button>
        </form>
      </motion.div>
    </motion.div>
  );
}

function shade(hex, percent) {
  const c = hex.replace("#", "");
  let r = parseInt(c.slice(0, 2), 16);
  let g = parseInt(c.slice(2, 4), 16);
  let b = parseInt(c.slice(4, 6), 16);
  const f = percent / 100;
  r = Math.max(0, Math.min(255, Math.round(r + 255 * f)));
  g = Math.max(0, Math.min(255, Math.round(g + 255 * f)));
  b = Math.max(0, Math.min(255, Math.round(b + 255 * f)));
  return "#" + [r, g, b].map((v) => v.toString(16).padStart(2, "0")).join("");
}
